/**
 * Pool pressure — requirement against qualified capacity, per resource pool.
 * Pure judgment library shared by D2 (the marginal question at admission) and
 * D3 (the impossible-portfolio ledger).
 *
 * PRD: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md §5–§6.
 * Per-commitment state: src/lib/commitmentState.ts.
 *
 * NO NEW CAPACITY MATH (PRD §1). Capacity comes from the engine's own
 * `resourceSummaries`; requirement comes from the recipes' demand templates
 * through `computeRecipeResourceDemand`. This module only subtracts, and says
 * plainly what the subtraction means.
 *
 * The two-pass discipline is the caller's, deliberately: the engine already
 * runs optimistic (raw pools) and conservative (pools less the capacity
 * buffer), so this is called once per pass rather than inventing a third
 * notion of capacity here. Stephens' "individually rational initiatives,
 * collectively impossible portfolio" is only visible when both passes are on
 * screen together.
 *
 * Pure on purpose: no React, no Supabase, no stores.
 */

import type { ResourceRequirementLine } from './commitmentState';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export interface PoolCapacityLine {
  resourceType: string;
  /** Hours the pool holds under THIS pass (raw, or less the buffer). */
  capacityHours: number;
}

/** One commitment's recipe-derived demand, keyed on items.id. */
export interface CommitmentDemand {
  itemId: string;
  requirements: readonly ResourceRequirementLine[];
}

/** Hours actually bound to a pool by live capacity claims. */
export interface PoolClaimLine {
  resourceType: string;
  claimedHours: number;
}

export interface PoolPressureInput {
  capacities: readonly PoolCapacityLine[];
  demands: readonly CommitmentDemand[];
  claims?: readonly PoolClaimLine[];
  /**
   * Share of a pool's capacity that may be committed before the pool counts as
   * over. Default 1 (100%); Patel's orgs may hold 0.9. PRD open question 1.
   */
  thresholdPercent?: number;
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

export type PoolState =
  /** Required work fits under the threshold. */
  | 'within'
  /** Required work exactly meets the threshold — no room left, not yet over. */
  | 'at_threshold'
  /** Required work exceeds the threshold: structurally impossible as committed. */
  | 'over'
  /** Work draws on this pool but no capacity for it is on file. */
  | 'no_capacity_on_file';

export interface PoolPressureRow {
  resourceType: string;
  capacityHours: number;
  thresholdHours: number;
  requiredHours: number;
  claimedHours: number;
  /** Hours the requirement exceeds the threshold by. 0 when not over. */
  overByHours: number;
  /** requiredHours / capacityHours, or null when there is no capacity on file. */
  utilization: number | null;
  state: PoolState;
  /** Commitments (items.id) drawing on this pool. */
  drawingItemIds: readonly string[];
  /** One plain sentence. Never says "cancel this" — the system picks no victim. */
  sentence: string;
}

export const DEFAULT_POOL_THRESHOLD = 1;

// ---------------------------------------------------------------------------
// The ledger
// ---------------------------------------------------------------------------

/**
 * One pass of the pool ledger. Pools with capacity on file but no demand are
 * still returned (an idle pool is information); pools with demand but no
 * capacity on file are returned as `no_capacity_on_file` rather than as
 * infinitely over — an absent number is not a zero.
 */
export function computePoolPressure(input: PoolPressureInput): PoolPressureRow[] {
  const threshold = input.thresholdPercent ?? DEFAULT_POOL_THRESHOLD;

  const capacityByType = new Map<string, number>();
  for (const c of input.capacities) {
    if (!c.resourceType) continue;
    capacityByType.set(c.resourceType, (capacityByType.get(c.resourceType) ?? 0) + c.capacityHours);
  }

  const claimedByType = new Map<string, number>();
  for (const c of input.claims ?? []) {
    if (!c.resourceType) continue;
    claimedByType.set(c.resourceType, (claimedByType.get(c.resourceType) ?? 0) + c.claimedHours);
  }

  const requiredByType = new Map<string, number>();
  const drawingByType = new Map<string, string[]>();
  for (const demand of input.demands) {
    for (const line of demand.requirements) {
      if (!line.resourceType || !(line.hours > 0)) continue;
      requiredByType.set(line.resourceType, (requiredByType.get(line.resourceType) ?? 0) + line.hours);
      const drawing = drawingByType.get(line.resourceType) ?? [];
      if (!drawing.includes(demand.itemId)) drawing.push(demand.itemId);
      drawingByType.set(line.resourceType, drawing);
    }
  }

  const types = new Set<string>([
    ...capacityByType.keys(),
    ...requiredByType.keys(),
    ...claimedByType.keys(),
  ]);

  const rows: PoolPressureRow[] = [];
  for (const resourceType of types) {
    const capacityHours = capacityByType.get(resourceType) ?? 0;
    const requiredHours = requiredByType.get(resourceType) ?? 0;
    const claimedHours = claimedByType.get(resourceType) ?? 0;
    const drawingItemIds = drawingByType.get(resourceType) ?? [];
    const hasCapacity = capacityByType.has(resourceType) && capacityHours > 0;
    const thresholdHours = hasCapacity ? capacityHours * threshold : 0;

    let state: PoolState;
    let overByHours = 0;
    if (!hasCapacity) {
      state = requiredHours > 0 ? 'no_capacity_on_file' : 'within';
    } else if (requiredHours > thresholdHours) {
      state = 'over';
      overByHours = requiredHours - thresholdHours;
    } else if (requiredHours === thresholdHours && requiredHours > 0) {
      state = 'at_threshold';
    } else {
      state = 'within';
    }

    rows.push({
      resourceType,
      capacityHours,
      thresholdHours,
      requiredHours,
      claimedHours,
      overByHours,
      utilization: hasCapacity ? requiredHours / capacityHours : null,
      state,
      drawingItemIds,
      sentence: describePool(resourceType, state, requiredHours, capacityHours, overByHours, drawingItemIds.length),
    });
  }

  return rows.sort((a, b) => b.overByHours - a.overByHours || a.resourceType.localeCompare(b.resourceType));
}

/**
 * Commitments drawing on a pool that cannot carry them — the D3 flag. Reads
 * "structurally impossible as committed", never "cancel this": which one gives
 * way is a judgment the system does not make.
 */
export function commitmentsOnOverloadedPools(
  rows: readonly PoolPressureRow[]
): Map<string, string[]> {
  const byItem = new Map<string, string[]>();
  for (const row of rows) {
    if (row.state !== 'over') continue;
    for (const itemId of row.drawingItemIds) {
      const pools = byItem.get(itemId) ?? [];
      pools.push(row.resourceType);
      byItem.set(itemId, pools);
    }
  }
  return byItem;
}

// ---------------------------------------------------------------------------
// D2 — the marginal question
// ---------------------------------------------------------------------------

export interface AdmissionImpactInput {
  /** The pool ledger as it stands BEFORE this admission. */
  rows: readonly PoolPressureRow[];
  /** What the admitted commitment needs, per pool. */
  admittedRequirement: readonly ResourceRequirementLine[];
}

export interface AdmissionPoolImpact {
  resourceType: string;
  capacityHours: number;
  thresholdHours: number;
  /** Required hours in the pool before this admission. */
  requiredBeforeHours: number;
  /** What the admission adds. */
  marginalHours: number;
  requiredAfterHours: number;
  /** Hours the admission pushes the pool past its threshold by. 0 if it fits. */
  shortfallHours: number;
  /** True when the pool was ALREADY over before this admission. */
  alreadyOver: boolean;
  /** True when no capacity for this pool is on file — a gap, not a zero. */
  noCapacityOnFile: boolean;
}

export interface AdmissionImpact {
  pools: readonly AdmissionPoolImpact[];
  /** Total hours the admission puts pools over by — what must be answered. */
  shortfallHours: number;
  /** True when a displacement answer is required to complete the admission. */
  requiresDisplacement: boolean;
  /** Pools the admission draws on that have no capacity on file. */
  poolsWithoutCapacity: readonly string[];
  sentence: string;
}

/**
 * What this admission costs, pool by pool. Computes the shortfall; enumerates
 * nothing else. It does not pick what comes off, does not rank candidates, and
 * does not decide — the ceremony asks a human, always (PRD non-goals).
 *
 * A pool already over before this admission stays flagged, but only the
 * MARGINAL push counts toward the shortfall this admission must answer for:
 * an admission is accountable for what it adds, not for a debt it inherited.
 */
export function computeAdmissionImpact(input: AdmissionImpactInput): AdmissionImpact {
  const byType = new Map(input.rows.map((r) => [r.resourceType, r]));
  const pools: AdmissionPoolImpact[] = [];
  const poolsWithoutCapacity: string[] = [];
  let shortfall = 0;

  for (const line of input.admittedRequirement) {
    if (!line.resourceType || !(line.hours > 0)) continue;
    const row = byType.get(line.resourceType);
    const capacityHours = row?.capacityHours ?? 0;
    const noCapacityOnFile = !row || row.state === 'no_capacity_on_file' || capacityHours <= 0;
    const thresholdHours = row?.thresholdHours ?? 0;
    const before = row?.requiredHours ?? 0;
    const after = before + line.hours;

    // Only the part of the overage this admission is responsible for.
    const overAfter = Math.max(0, after - thresholdHours);
    const overBefore = Math.max(0, before - thresholdHours);
    const marginalOver = noCapacityOnFile ? 0 : overAfter - overBefore;

    if (noCapacityOnFile) poolsWithoutCapacity.push(line.resourceType);
    shortfall += marginalOver;

    pools.push({
      resourceType: line.resourceType,
      capacityHours,
      thresholdHours,
      requiredBeforeHours: before,
      marginalHours: line.hours,
      requiredAfterHours: after,
      shortfallHours: marginalOver,
      alreadyOver: overBefore > 0,
      noCapacityOnFile,
    });
  }

  pools.sort((a, b) => b.shortfallHours - a.shortfallHours || a.resourceType.localeCompare(b.resourceType));

  return {
    pools,
    shortfallHours: shortfall,
    requiresDisplacement: shortfall > 0,
    poolsWithoutCapacity,
    sentence: describeImpact(pools, shortfall, poolsWithoutCapacity),
  };
}

// ---------------------------------------------------------------------------
// Sentences
// ---------------------------------------------------------------------------

function describePool(
  resourceType: string,
  state: PoolState,
  requiredHours: number,
  capacityHours: number,
  overByHours: number,
  drawingCount: number
): string {
  if (state === 'no_capacity_on_file') {
    return `${drawingCount} commitment${drawingCount === 1 ? '' : 's'} draw${drawingCount === 1 ? 's' : ''} on ${resourceType}, but no capacity for it is on file.`;
  }
  if (state === 'over') {
    return `${resourceType} is committed ${fmt(overByHours)} beyond what it can carry — ${fmt(requiredHours)} required against ${fmt(capacityHours)}. Structurally impossible as committed.`;
  }
  if (state === 'at_threshold') {
    return `${resourceType} is committed to its limit — ${fmt(requiredHours)} of ${fmt(capacityHours)}, with no room left.`;
  }
  if (requiredHours <= 0) {
    return `${resourceType} carries ${fmt(capacityHours)} and nothing is drawing on it.`;
  }
  return `${resourceType} fits — ${fmt(requiredHours)} required of ${fmt(capacityHours)}.`;
}

function describeImpact(
  pools: readonly AdmissionPoolImpact[],
  shortfall: number,
  poolsWithoutCapacity: readonly string[]
): string {
  if (pools.length === 0) {
    return 'This work has no resource requirement on file, so what it costs cannot be computed.';
  }
  if (shortfall <= 0) {
    const gap =
      poolsWithoutCapacity.length > 0
        ? ` No capacity is on file for ${poolsWithoutCapacity.join(', ')}, so that part is unmeasured.`
        : '';
    return `This fits within every pool it draws on.${gap}`;
  }
  const over = pools.filter((p) => p.shortfallHours > 0);
  const named = over
    .slice(0, 2)
    .map((p) => `${p.resourceType} by ${fmt(p.shortfallHours)}`)
    .join(', ');
  const more = over.length > 2 ? ` and ${over.length - 2} more` : '';
  return `Admitting this puts ${named}${more} past what it can carry — ${fmt(shortfall)} has to come from somewhere.`;
}

function fmt(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}h`;
}
